<template>
	<div class="client-role-modal">
		<app-loader
			v-if="isLoading"
			:style="loaderStyle"
		/>
		<div class="client-role-modal__search">
			<app-input
				ref="$input"
				v-model="filter"
				class="client-role-modal__search-input"
				:placeholder="$t('search_short')"
				show-search-button
			/>
			<button
				v-if="props.showCloseIcon"
				type="button"
				class="client-role-modal__close"
				@click="emit('close')"
			>
				<app-icon
					name="close"
					width="20px"
					color="#979797"
					hover-color="#499259"
					cursor="pointer"
				/>
			</button>
		</div>
		<div class="client-role-modal__header">
			<div class="client-role-modal__header-title">
				{{ $t('roles_label') }}
			</div>
		</div>
		<div
			class="client-role-modal__body"
			ref="$body"
		>
			<div
				v-for="role in filteredRoles"
				:key="role.Id"
				class="client-role-modal__body-item"
			>
				<app-checkbox
					:style="{ height: '16px', marginRight: '8px' }"
					:value="isRoleAdded(role.Id)"
					text=""
					@input="(on) => onToggleRole(on, role)"
				/>
				<div
					class="client-role-modal__body-item-text"
					@click="onToggleRole(!isRoleAdded(role.Id), role)"
				>
					{{ role.Name }}
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import {
	computed,
	nextTick,
	onMounted,
	ref
} from "vue";
import AppCheckbox from "@/components/ui-kit/checkbox/AppCheckbox.vue";
import AppInput from "@/components/ui-kit/input/AppInput.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import { CommonEntityRecord } from "@/models/interfaces/common";
import { searchStore } from "@/store/search";

const props = defineProps<{
	clientId: number,
	attachedRoles: CommonEntityRecord[],
	showCloseIcon?: boolean,
}>()

const emit = defineEmits(['change', 'close'])

const $input = ref<typeof AppInput | null>(null)
const $body = ref<HTMLDivElement | null>(null)

const store = searchStore()

const isLoading = ref(true)
const filter = ref('')

const roles = computed<CommonEntityRecord[]>(() => store.actualRoles)

const addedIds = computed(() => new Set((props.attachedRoles || []).map(r => r.Id)))

const filteredRoles = computed<CommonEntityRecord[]>(() => {
	const query = filter.value.trim().toLowerCase()
	if (!query) {
		return roles.value
	}

	return roles.value.filter(r => r.Name?.toLowerCase().includes(query))
})

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 0.5)'
}))

const isRoleAdded = (roleId: number) => addedIds.value.has(roleId)

const onToggleRole = (on: boolean, role: CommonEntityRecord) => {
	if (on) {
		store.attachRoleToSelectedClient(props.clientId, { Id: role.Id, Name: role.Name })
	} else {
		store.detachRoleFromSelectedClient(props.clientId, role.Id)
	}
	emit('change')
}

onMounted(async () => {
	try {
		isLoading.value = true
		await store.loadActualRoles()
	} finally {
		isLoading.value = false
	}
	await nextTick(() => $input.value?.focusInput())
})
</script>

<style scoped lang="scss">
.client-role-modal {
	display: flex;
	flex-direction: column;
	position: relative;
	flex: 1;
	min-height: 0;
	padding: 12px 0 0 12px;

	&__search {
		display: flex;
		gap: 8px;
		width: 100%;
		margin-bottom: 8px;
		padding-right: 12px;

		&-input {
			width: 100%;
		}
	}

	&__close {
		display: flex;
		flex-shrink: 0;
		justify-content: flex-end;
		width: 32px;
		height: 32px;
		padding: 0;
		border: 0;
		background: transparent;
		cursor: pointer;
	}

	&__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 8px;
		padding-right: 12px;
		color: #979797;

		&-title {
			font-size: 12px;
			line-height: 16px;
			font-weight: 600;
		}
	}

	&__body {
		display: flex;
		flex: 1;
		min-height: 0;
		flex-direction: column;
		gap: 8px;
		overflow: auto;
		padding-right: 12px;
		padding-bottom: 12px;

		&-item {
			display: flex;
			align-items: center;
			font-size: 14px;
			line-height: 18px;
			color: #333;
			cursor: pointer;
			transition: color 0.1s;

			&-text {
				font-size: 14px;
				line-height: 18px;
			}

			&:hover {
				text-decoration: underline;
				color: #396742;
			}
		}
	}
}
</style>
